import { useState, type ReactNode } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { apiFetch } from '../../../lib/api-client';
import { API_BASE } from '../../../lib/api-base';
import { refreshOutreaches } from '../../../lib/outreaches';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import { TextButton } from '../ui';
import styles from './outreach-thread.module.css';

/**
 * The way out of a message that needs nothing from you.
 *
 * Every surface files an inbound outreach under "waiting on you" from the SHAPE of the trail — the
 * thread is not COMPLETED and they spoke last. That is a proxy for "they asked you something", and the
 * two part company on the commonest reply of all: an answer to a question the USER asked. The receiving
 * agent is meant to judge the message on arrival and finish the thread itself, and when it does this
 * button is not here to be pressed. When it doesn't, there is no second chance — the user has nothing to
 * say, so nothing wakes the agent again — and until this shipped the ONLY way to clear the row was to
 * reply to it. Sending a message you did not want to send, to make a badge go away, is not a way out.
 *
 * So this is the same judgement, made by the reader, writing the same COMPLETED status the agent's own
 * `complete` writes. Nothing is optimistic: the row changes only after the write lands, because the
 * whole complaint here is a surface claiming a state that is not true.
 *
 * It is deliberately NOT destructive and asks for no confirmation. COMPLETED means "no work pending",
 * not "closed forever": if they write again the new message reopens the thread, so the worst a stray
 * press can do is hide a row until the next message arrives.
 */
export function OutreachNoReplyNeeded({ outreachId, onCompleted, labelKey, startIcon, align = 'start', pageName, refresh }: {
  outreachId: string;
  /** Fired once the thread is finished, so the dialog can close itself. */
  onCompleted: () => void;
  /** Defaults to "No reply needed". The conversation VIEWER says "Mark as replied" instead — there the
   *  reader is looking at the thread rather than at a task row, so the useful framing is what they are
   *  asserting about it, not what they are declining to do. */
  labelKey?: string;
  /** The design's prefix slot — a 16px lucide glyph, `aria-hidden`. */
  startIcon?: ReactNode;
  /** `end` right-aligns the row, for the viewer where it sits just above the composer. */
  align?: 'start' | 'end';
  /** The src:198 page this press belongs to. Defaults to the tasks page, where this shipped first. */
  pageName?: string;
  /** Re-read after the write lands, BEFORE `onCompleted` closes anything — so the surface the reader
   *  returns to already shows the thread settled. Defaults to the task-side outreach read; the viewer
   *  passes its own, because completing moves the thread's TURN and the contact lists badge on it. */
  refresh?: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const complete = async () => {
    if (busy) return;
    logPageClickBi({ ...TASKS_PAGE, ...(pageName ? { page_name: pageName } : {}), button_name: 'outreach_no_reply_needed' });
    setBusy(true);
    setFailed(false);
    try {
      const res = await apiFetch(`${API_BASE}/api/outreach-complete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outreachId }),
      });
      if (!res.ok) throw new Error(`complete ${res.status}`);
      // Re-read before closing, so the list the user lands back on already shows the row settled rather
      // than still red until the next ambient refresh.
      await (refresh ? refresh() : refreshOutreaches()).catch(() => undefined);
      onCompleted();
    } catch {
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <div className={align === 'end' ? `${styles.noReplyRow} ${styles.noReplyRowEnd}` : styles.noReplyRow}>
      {/* Announced, because the only other evidence of a failure is a dialog that stayed open — which
          reads as a button that does nothing. ABOVE the button, where both phones put it: a reader who
          presses again should have read why the first press did not take. */}
      {failed ? (
        <p className={styles.failed} role="alert">
          {t('tasks.outreach.noReplyNeeded.failed')}
        </p>
      ) : null}
      {/* `loading`, NOT `disabled`: the write takes a round trip, and a button that merely greys out
          says "unavailable" where it should say "working". `loading` blocks the click too, so the
          double-tap guard `if (busy) return` is belt and braces rather than the only defence. */}
      <TextButton size="small" startIcon={startIcon} onClick={complete} loading={busy}>
        {t(labelKey ?? 'tasks.outreach.noReplyNeeded')}
      </TextButton>
    </div>
  );
}
